import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { toast } from "sonner";
import { Bookmark, Trash2, ChevronRight, ChevronDown, Users, Loader2, ThumbsUp, ThumbsDown, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AddToCampaignPopover } from "@/components/sales/AddToCampaignPopover";
import RejectReasonModal from "@/components/common/RejectReasonModal";

export default function Shortlists() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);
    const [details, setDetails] = useState({});
    // Reject modal state — {candidate} or null
    const [pendingReject, setPendingReject] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = () => api.get("/shortlists").then((r) => { setRows(r.data); setLoading(false); });
    useEffect(() => { load(); }, []);

    const toggle = async (id) => {
        if (open === id) { setOpen(null); return; }
        setOpen(id);
        if (!details[id]) {
            const r = await api.get(`/shortlists/${id}`);
            setDetails((d) => ({...d, [id]: r.data}));
        }
    };

    const del = async (id) => {
        if (!confirm(t("shortlists.delete_confirm"))) return;
        try { await api.delete(`/shortlists/${id}`); toast.success(t("shortlists.deleted")); load(); }
        catch (e) { toast.error(t("team.failed")); }
    };

    const approveCandidate = async (candidate) => {
        setBusyId(candidate.id);
        try {
            await api.post(`/candidates/${candidate.id}/approve`);
            toast.success(t("shortlists.approved_toast", { name: candidate.name }));
            // Refresh the currently opened shortlist details so the stage badge updates
            if (open) {
                const r = await api.get(`/shortlists/${open}`);
                setDetails((d) => ({ ...d, [open]: r.data }));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || t("shortlists.approve_failed")); }
        finally { setBusyId(null); }
    };
    const confirmRejectCandidate = async (reason, category, remember) => {
        if (!pendingReject?.candidate) return;
        try {
            await api.post(`/candidates/${pendingReject.candidate.id}/reject`, { reason, category, remember });
            toast.success(t("shortlists.rejected_toast"));
            if (open) {
                const r = await api.get(`/shortlists/${open}`);
                setDetails((d) => ({ ...d, [open]: r.data }));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || t("shortlists.reject_failed")); }
    };

    return (
        <PageHeader title={t("shortlists.title")} subtitle={t("shortlists.subtitle")}>
            {loading ? <div className="text-[var(--jr-text-2)]">{t("common.loading")}</div> :
                rows.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-white/15 bg-[var(--jr-surface)] p-10 text-center">
                        <Bookmark className="text-[var(--jr-amber)] mx-auto" size={28}/>
                        <p className="mt-4 text-[var(--jr-text-2)]">{t("shortlists.empty_pre")} <Link to="/app/recruit/search" className="text-[var(--jr-amber)] hover:underline">Talent Search</Link> {t("shortlists.empty_post")}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((r) => {
                            const isOpen = open === r.id;
                            const d = details[r.id];
                            return (
                                <div key={r.id} data-testid={`shortlist-${r.id}`} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
                                    <button onClick={() => toggle(r.id)} data-testid={`shortlist-toggle-${r.id}`} className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-white/3">
                                        {isOpen ? <ChevronDown size={16} className="text-[var(--jr-text-2)]"/> : <ChevronRight size={16} className="text-[var(--jr-text-2)]"/>}
                                        <div className="flex-1 text-left min-w-0">
                                            <div className="font-display text-lg tracking-tight">{r.name}</div>
                                            <div className="text-xs text-[var(--jr-text-2)] truncate">{r.role_title || r.description}</div>
                                        </div>
                                        <div className="inline-flex items-center gap-2 text-xs text-[var(--jr-text-2)] font-mono uppercase tracking-[0.16em]">
                                            <Users size={12}/> {(r.candidate_ids || []).length}
                                        </div>
                                        <Trash2 onClick={(e) => { e.stopPropagation(); del(r.id); }} data-testid={`shortlist-delete-${r.id}`} size={14} className="text-[var(--jr-text-3)] hover:text-[var(--jr-error)] cursor-pointer"/>
                                    </button>
                                    {isOpen && (
                                        <div className="px-5 pb-5 border-t border-white/8 pt-4">
                                            {!d ? <div className="text-sm text-[var(--jr-text-2)] inline-flex items-center gap-2"><Loader2 className="animate-spin" size={12}/> {t("common.loading")}</div> :
                                                <>
                                                    <div className="flex items-center justify-end mb-3">
                                                        <AddToCampaignPopover
                                                            entityType="candidate"
                                                            entityIds={(d.candidates || []).map((c) => c.id)}
                                                            onEnrolled={load}
                                                            testidPrefix={`shortlist-atc-${r.id}`}
                                                            buttonLabel={t("shortlists.add_n_candidates", { n: (d.candidates || []).length })} />
                                                    </div>
                                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                        {(d.candidates || []).map((c) => {
                                                            const declined = c.stage === "declined";
                                                            const approved = c.stage && c.stage !== "sourced" && c.stage !== "declined";
                                                            return (
                                                                <div key={c.id} data-testid={`candidate-card-${c.id}`}
                                                                    className={`rounded-lg border p-3 ${declined ? "border-dashed border-white/10 bg-[var(--jr-surface-2)]/60 opacity-60" : "border-white/10 bg-[var(--jr-surface-2)] hover:border-white/20 transition-colors"}`}>
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <Link to={`/app/recruit/candidate/${c.id}`}
                                                                            data-testid={`shortlist-cand-open-${c.id}`}
                                                                            className="min-w-0 flex-1 group">
                                                                            <div className="text-sm font-medium text-white truncate group-hover:text-[var(--jr-amber)] transition-colors">{c.name}</div>
                                                                            <div className="text-xs text-[var(--jr-text-2)] truncate">{c.title}</div>
                                                                        </Link>
                                                                        {approved && (
                                                                            <Link to={`/app/recruit/candidate/${c.id}?tab=outreach`}
                                                                                data-testid={`shortlist-activity-${c.id}`}
                                                                                title={t("shortlists.view_activity")}
                                                                                className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[#22c55e]/15 text-[#22c55e] shrink-0 hover:bg-[#22c55e]/30 transition-colors">
                                                                                ✓ {c.stage} <ArrowUpRight size={9}/>
                                                                            </Link>
                                                                        )}
                                                                        {declined && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-[var(--jr-error)]/15 text-[var(--jr-error)] shrink-0">{t("shortlists.declined")}</span>}
                                                                    </div>
                                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                                        {(c.skills || []).slice(0, 3).map((s, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-3)]">{s}</span>)}
                                                                    </div>
                                                                    {!declined && (
                                                                        <div className="mt-2 flex items-center gap-1.5">
                                                                            <button onClick={() => setPendingReject({ candidate: c })}
                                                                                data-testid={`candidate-reject-${c.id}`}
                                                                                title={t("shortlists.reject_title")}
                                                                                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/20">
                                                                                <ThumbsDown size={10} /> {t("common.reject")}
                                                                            </button>
                                                                            <button onClick={() => approveCandidate(c)}
                                                                                disabled={busyId === c.id || approved}
                                                                                data-testid={`candidate-approve-${c.id}`}
                                                                                title={t("shortlists.approve_title")}
                                                                                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#22c55e] text-black font-medium hover:bg-[#16a34a] disabled:opacity-50">
                                                                                {busyId === c.id ? <Loader2 size={10} className="animate-spin" /> : <ThumbsUp size={10} />} {t("common.approve")}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            {/* Reject-with-reason modal */}
            <RejectReasonModal
                open={!!pendingReject}
                onClose={() => { setPendingReject(null); load(); }}
                title={t("shortlists.reject_modal_title")}
                subtitle={pendingReject?.candidate ? `${pendingReject.candidate.name} — ${pendingReject.candidate.title}` : ""}
                originalSnippet={pendingReject?.candidate ? `${pendingReject.candidate.title} · ${pendingReject.candidate.seniority || ""} · ${(pendingReject.candidate.skills || []).slice(0, 5).join(", ")}` : ""}
                onConfirm={confirmRejectCandidate}
                confirmLabel={t("shortlists.reject_confirm_label")}
            />
        </PageHeader>
    );
}
